import type { TicketTrackingLine } from '~/domain/plugins/services/tracker/events';

/** The catalogue fields a tracking line needs. Kept structural so both the
 *  domain `TicketVariant` and the UI's `TicketVariantOption` (what the booking
 *  dialog holds) satisfy it. */
type TrackableVariant = {
  id: string;
  name: string;
  price?: { vatInclusivePriceCents: number; currency: string } | null;
  addOns?: readonly {
    id: string;
    name: string;
    price?: { vatInclusivePriceCents: number; currency: string } | null;
  }[];
};

/**
 * Maps a basket ticket selection to the lines the GTM ecommerce payload
 * builders take. Ticket entries carry variant *and* add-on ids — both are
 * resolved against the experience's variants, matching `buildTicketSummary`'s
 * id handling. Unknown ids and zero quantities are skipped.
 *
 * Ids are emitted with the `variant/` / `addon/` kind prefix the payload
 * builders expect: internally the app keys tickets by bare id and only adds the
 * prefix at service boundaries (`prefixTicketIds`), but the GTM container was
 * built against the legacy storefront, which carried the prefix all the way
 * into the dataLayer (US-11215).
 *
 * `currency` is read off the first priced variant; `null` means nothing is
 * priced (free experience) and the caller should skip the push.
 */
export function ticketTrackingLines(
  tickets: readonly { variantId: string; quantity: number }[],
  variants: readonly TrackableVariant[]
): { lines: TicketTrackingLine[]; currency: string | null } {
  const byId = new Map<
    string,
    { prefixedId: string; name: string; unitPriceCents: number }
  >();
  for (const variant of variants) {
    byId.set(variant.id, {
      prefixedId: `variant/${variant.id}`,
      name: variant.name,
      unitPriceCents: variant.price?.vatInclusivePriceCents ?? 0,
    });
    for (const addOn of variant.addOns ?? []) {
      byId.set(addOn.id, {
        prefixedId: `addon/${addOn.id}`,
        name: addOn.name,
        unitPriceCents: addOn.price?.vatInclusivePriceCents ?? 0,
      });
    }
  }

  const lines: TicketTrackingLine[] = [];
  for (const ticket of tickets) {
    if (ticket.quantity <= 0) continue;
    const entry = byId.get(ticket.variantId);
    if (!entry) continue;
    lines.push({
      variantId: entry.prefixedId,
      variantName: entry.name,
      unitPriceCents: entry.unitPriceCents,
      quantity: ticket.quantity,
    });
  }

  const currency =
    variants.find((v) => v.price?.currency)?.price?.currency ?? null;
  return { lines, currency };
}
